<template>
  <div class="checkbox" :class="[variant, {hasError, hasSuccess, isChecked, isDisabled: ($attrs.disabled!==undefined && $attrs.disabled!==false)}]">
    <div class="input-wrapper">
      <input
        :id="inputId"
        v-model="computedModelValue"
        type="checkbox"
        v-bind="$attrs"
      />
      <label :for="inputId">
        <slot v-if="$slots.label" name="label"></slot>
        <span v-else v-html="props.label"></span>
      </label>
    </div>
    <div v-if="hasError && errorMessage" class="text-danger text-s message-wrapper message-error">
      <span>{{ errorMessage }}</span>
      <Icon name="warning-fill" color="danger" />
    </div>
  </div>
</template>


<script setup>
import { inject, computed, useAttrs, ref, useId } from 'vue'
import Icon from '../Icon'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: {
    type: [Boolean, Number, Array],
    default: false,
  },
  label: {
    type: String,
    default: null,
  },
  errorMessage: {
    type: String,
    default: null,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  hasSuccess: {
    type: Boolean,
    default: false,
  },
  trackingLabel: {
    type: String,
    default: null,
  },
  variant: {
    type: String,
    default: 'normal',
    validator: (value) => ['normal', 'advanced', 'bordered', ''].includes(value),
  },
})

const attrs = useAttrs()
const inputId = attrs.id ? attrs.id : useId()

const isChecked = ref(false)
const emit = defineEmits(['update:modelValue'])
const setIsCheckedState = (newValue) => {
  isChecked.value = (typeof newValue==='boolean' && newValue) || (typeof newValue!=='boolean' && newValue.length > 0)
}

const Track = inject('track', props.trackingLabel)

const track = (action) => {
  if (props.trackingLabel) {
    Track(`form-checkbox-${action}`, props.trackingLabel)
  }
}

const computedModelValue = computed({
  get() {
    return props.modelValue
  },
  set(newValue) {
    setIsCheckedState(newValue)
    track(isChecked.value ? 'select' : 'deselect')
    emit('update:modelValue', newValue)
  },
})
setIsCheckedState(props.modelValue)
</script>


<style scoped>
.checkbox {
  display: flex;
  flex-direction: column;
  gap: .25rem;

  --checkboxSize: 1.333rem;
  --checkboxBorderColor: var(--mint-dark);
  --tickHeight: .55rem;
  --tickWidth: .25rem;
  --tickThinkness: .15rem;
  --tickColor: var(--white);
}

.checkbox .input-wrapper {
  display: grid;
  grid-template-columns: var(--checkboxSize) 1fr;
  gap: 1rem;
  transition-duration: var(--time1);
  transition-timing-function: ease-in-out;
  transition-property: background-color, border-color;
}

.checkbox .input-wrapper input {
  display: flex;
  appearance: none;
  height: var(--checkboxSize);
  width: var(--checkboxSize);
  margin: 0;
  background-color: var(--white);
  border: 1px solid var(--checkboxBorderColor);
  border-radius: .25rem;
  font: inherit;
  color: var(--white);
  cursor: pointer;
  position: relative;
}

.checkbox .input-wrapper input:not(:disabled):is(:active, :focus) {
  box-shadow: 0 0 0 1px var(--mint-dark);
}

.checkbox .input-wrapper input:disabled {
  cursor: not-allowed;

  --tickColor: var(--neutral-20);
  --checkboxBorderColor: var(--neutral-20);
  background-color: var(--neutral-10);
}

.checkbox .input-wrapper input:checked:not(:disabled) {
  border-color: var(--mint-lighter);
  background-color: var(--success);
}

.checkbox .input-wrapper input:checked::before {
  box-sizing: initial;
  content: "";
  position: absolute;
  top: calc(var(--checkboxSize) * 0.5 - var(--tickThinkness));
  left: calc(var(--checkboxSize) * 0.5 - var(--tickHeight) + var(--tickThinkness));
  width: 0;
  height: 0;
  flex-shrink: 0;
  border-right: var(--tickThinkness) solid var(--transparent);
  border-bottom: var(--tickThinkness) solid var(--transparent);
  transform: rotate(45deg);
  transform-origin: 0 100%;
  animation: checkbox-check var(--time2) ease-in-out forwards;
}

@keyframes checkbox-check{
  0%{
    width: 0;
    height: 0;
    border-color: var(--tickColor);
    transform: translate3d(0, 0, 0) rotate(45deg);
  }

  33%{
    width: var(--tickWidth);
    height: 0;
    transform: translate3d(0, 0, 0) rotate(45deg);
  }

  100%{
    width: var(--tickWidth);
    height: var(--tickHeight);
    border-color: var(--tickColor);
    transform: translate3d(0, calc(var(--tickHeight) * -1), 0) rotate(45deg);
  }
}

.checkbox .input-wrapper input:disabled + label {
  color: var(--neutral-40);
}

.checkbox.hasError .input-wrapper input:not(:disabled) {
  --checkboxBorderColor: var(--danger);
}

.checkbox.hasSuccess .input-wrapper input:not(:disabled) {
  --checkboxBorderColor: var(--success);
}

.checkbox:is(.advanced, .bordered) .input-wrapper {
  padding: 1rem;
  border-radius: .25rem;
  border: 1px solid var(--mint-dark);
}

.checkbox:is(.advanced, .bordered) .input-wrapper:hover,
.checkbox:is(.advanced, .bordered):focus-within .input-wrapper {
  border-color: var(--mint-darker);
}

.checkbox:is(.advanced, .bordered).hasError .input-wrapper {
  border-color: var(--danger);
}

.checkbox.advanced .input-wrapper {
  background-color: var(--mint-lightest);
}

.checkbox.advanced .input-wrapper:hover,
.checkbox.advanced:focus-within .input-wrapper {
  background-color: var(--mint-light);
}

.checkbox.advanced.isChecked .input-wrapper{
  background-color: var(--mint);
  border-color: var(--mint);
}

.checkbox.advanced.isChecked .input-wrapper:hover,
.checkbox.advanced.isChecked:focus-within .input-wrapper {
  border-color: var(--mint-darker);
}

.checkbox.advanced .input-wrapper input + label {
  font-weight: 700;
}

.checkbox .message-wrapper {
  display: grid;
  grid-template-columns: 1fr 1rem;
  gap: .25rem;
  padding-right: .75rem;
}
</style>
